import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { heroRotatingWords } from '../data/mockData';
import FeaturedCaseStudy from './FeaturedCaseStudy';

const HeroSection = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const rotateWord = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentWordIndex((prev) => (prev + 1) % heroRotatingWords.length);
      setIsAnimating(false);
    }, 400);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotateWord, 3000);
    return () => clearInterval(interval);
  }, [rotateWord]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Generate sparkle data once so positions don't shift on re-render
  const sparkles = useMemo(() =>
    Array.from({ length: 45 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 4 + 1.5,
      delay: Math.random() * 6,
      duration: Math.random() * 3 + 2,
      color: i % 5 === 0 ? '#FFD700' : i % 5 === 1 ? '#ff8c42' : '#ffffff',
      opacity: Math.random() * 0.7 + 0.3,
    })), []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-start overflow-hidden"
    >
      {/* Background Image - Robot Mascot */}
      <div className="absolute inset-0">
        <img
          src="/hero-bg.webp"
          alt="Imprint Robot Mascot"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center center' }}
        />
        {/* Dark overlay on the left for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#040810]/90 via-[#040810]/60 to-transparent"></div>
        {/* Subtle overlay across all for depth */}
        <div className="absolute inset-0 bg-[#040810]/20"></div>
      </div>

      {/* Animated Sparkles Overlay */}
      <div className="absolute inset-0 z-[2] pointer-events-none">
        {sparkles.map((s) => (
          <div
            key={s.id}
            className="absolute rounded-full"
            style={{
              left: s.left + '%',
              top: s.top + '%',
              width: s.size + 'px',
              height: s.size + 'px',
              background: s.color,
              opacity: 0,
              animation: `sparkle-twinkle ${s.duration}s ease-in-out infinite ${s.delay}s`,
              boxShadow: `0 0 ${s.size * 3}px ${s.color}`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 section-container pt-8 lg:pt-44 pb-20 lg:pb-32">
        <div className="max-w-3xl">
          {/* Main Heading */}
          <h1 className="mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="block text-5xl md:text-7xl lg:text-8xl font-bold text-[#00BCE4] leading-tight"
              style={{ textShadow: '0 2px 30px rgba(0,188,228,0.3)' }}>
              The future
            </span>
            <span className="block text-5xl md:text-7xl lg:text-8xl font-bold leading-tight">
              <span className="text-[#00BCE4]" style={{ textShadow: '0 2px 30px rgba(0,188,228,0.3)' }}>of </span>
              <span
                className={`inline-block text-white ${
                  isAnimating ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
                }`}
                style={{
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                  textShadow: '0 2px 20px rgba(255,255,255,0.2)',
                }}
              >
                {heroRotatingWords[currentWordIndex]}
              </span>
            </span>
            <span className="block text-5xl md:text-7xl lg:text-8xl font-bold text-[#00BCE4] leading-tight"
              style={{ textShadow: '0 2px 30px rgba(0,188,228,0.3)' }}>
              printing
            </span>
          </h1>

          {/* Subtitle */}
          <div className="space-y-3 mb-10">
            <h3 className="text-lg md:text-xl text-gray-200 font-medium leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              Partner with Imprint the Integration and Print & Packaging MIS specialists
            </h3>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              Providing solutions to maximise your efficiencies and strengthen your company for the long term
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollToSection('contact')}
              className="btn-primary text-sm"
            >
              Tell Me More
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => scrollToSection('solutions')}
              className="btn-outline text-sm border-white/30 text-white hover:bg-white/10 hover:border-white/50"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}
            >
              Explore Solutions
            </button>
          </div>

          {/* Auto-rotating featured case study (social proof) */}
          <FeaturedCaseStudy />
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0b10] to-transparent z-[3]"></div>
    </section>
  );
};

export default HeroSection;
